import { useEffect, useState } from 'react';
import {
    GetDrawDownRepaymentScheduleCreditFacilityResponse,
    GetFormalOffersResponse,
    LapiError,
    fetchGetDrawDownRepaymentScheduleCreditFacility
} from '@iwoca/lapi-client/edge';
import {
    AlertBox,
    Button,
    Card,
    Icon,
    Radio,
    Spinner,
    Tooltip
} from '@iwoca/orion';
import { useQuery } from '@tanstack/react-query';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import { intlFormatCurrency } from '@iwIntlProvider/FormattedCurrency';
import { FormattedMessage, useIntl } from 'react-intl';
import styles from './flexiCard.module.css';
import messages from './flexiCard.messages';
import FlexiScheduleModal from '../flexiScheduleModal/flexiScheduleModal';
import Fetching from '../../../../ui/fetching/fetching';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { getLoanDurationInMonths } from '../../../../../myAccount/myOffers/MyOffers.helpers';
import { REQUEST_CHANGE_ROUTER } from '@iwRouter/IwRouter.actions';
import { buildLocationWithSearch } from '../../../../drawDown/DrawDownHelper';
import classNames from 'classnames';
import { tracking } from '@iwoca/frontend-tracking-library';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    startDrawdown: (offerId: string, drawdownAmount: number) => {
        dispatch({
            type: REQUEST_CHANGE_ROUTER,
            //@ts-ignore
            location: buildLocationWithSearch({
                pathname: '/funding-requirements/',
                offerId: offerId,
                amount: drawdownAmount
            })
        });
    }
});

type TSchedule = GetDrawDownRepaymentScheduleCreditFacilityResponse;

type TFlexiCard = {
    offers:
        | NonNullable<GetFormalOffersResponse['data']>['formal_offers'][0]
        | NonNullable<GetFormalOffersResponse['data']>['formal_offers'];
    drawdownAmount?: number;
    copyMode:
        | 'rbl_xsell'
        | 'interest_only_xsell'
        | 'longer_loans_xsell'
        | 'shorter_loans_xsell'
        | 'flexi24_xsell'
        | 'default';
} & ReturnType<typeof mapDispatchToProps>;

const FlexiCard = ({
    offers,
    drawdownAmount,
    startDrawdown,
    copyMode
}: TFlexiCard) => {
    const stateKey = useStateKey();
    const intl = useIntl();
    const minAmount = 150;

    const isMultiOffer = offers instanceof Array;

    const getDefaultOffer = () => {
        if (isMultiOffer) {
            const minDurationOffer = offers.reduce(function (
                minDurationOffer,
                offer
            ) {
                return (minDurationOffer.duration?.amount || 0) <
                    offer.duration?.amount!
                    ? minDurationOffer
                    : offer;
            }, {});
            return minDurationOffer;
        } else return offers;
    };

    const [openSchedule, setOpenSchedule] = useState(false);
    const [selectedOffer, setSelectedOffer] = useState(getDefaultOffer());

    const offersInRange = () => {
        if (!drawdownAmount) return true;
        if (!isMultiOffer) {
            return offers.max_amount! >= drawdownAmount;
        } else
            return Boolean(
                offers.find((offer) => offer.max_amount! >= drawdownAmount)
            );
    };

    // if drawdownAmount chosen is out of range of current offer find first available offer
    useEffect(() => {
        const currentMaxAmount = selectedOffer?.max_amount || 0;
        if (
            drawdownAmount &&
            (drawdownAmount > currentMaxAmount || drawdownAmount < 150) &&
            isMultiOffer
        ) {
            setSelectedOffer(
                offers.find(
                    (offer) =>
                        drawdownAmount <= offer.max_amount! &&
                        drawdownAmount >= minAmount
                )!
            );
        }
    }, [drawdownAmount, selectedOffer, offers, isMultiOffer]);

    const { data: schedule, isFetching } = useQuery<TSchedule, LapiError>({
        queryKey: [
            'drawDownSchedule',
            stateKey,
            selectedOffer?.offer_id,
            drawdownAmount,
            minAmount,
            selectedOffer?.max_amount
        ],
        queryFn: (): Promise<TSchedule> => {
            const amountInRange =
                !!drawdownAmount &&
                drawdownAmount >= minAmount &&
                drawdownAmount <= selectedOffer?.max_amount!;
            if (amountInRange) {
                return fetchGetDrawDownRepaymentScheduleCreditFacility({
                    stateKey: stateKey,
                    offerId: selectedOffer?.offer_id!,
                    query: {
                        amount: drawdownAmount!,
                        early_final_repayment_date: undefined
                    }
                });
            } else {
                return new Promise((res) => res(schedule!));
            }
        },
        enabled: !!drawdownAmount,
        keepPreviousData: true
    });

    if (!drawdownAmount || !schedule) {
        return (
            <Card className={styles.card}>
                <Spinner className={styles.spinner} />
            </Card>
        );
    }

    const pricingMessage = () => {
        if (copyMode === 'rbl_xsell') return messages.RBLXsellPricing;
        else return messages.XsellPricing;
    };

    const benefitMessage = () => {
        switch (copyMode) {
            case 'rbl_xsell':
                return messages.RBLCopyModeBenefitText;
            case 'longer_loans_xsell':
                return messages.longerLoansCopyModeBenefitText;
            case 'shorter_loans_xsell':
                return messages.shorterLoansCopyModeBenefitText;
            case 'interest_only_xsell':
                return messages.interestOnlyCopyModeBenefitText;
            case 'flexi24_xsell':
                return messages.flexi24CopyModeBenefitText;
            case 'default':
                return messages.defaultFlexiCopyModeBenefitText;
        }
    };

    const interestMessage = () => {
        if (copyMode === 'rbl_xsell') return messages.RBLXsellInterest;
        else return messages.xSellInterest;
    };

    return (
        <>
            <Card
                data-testid={`flexiCard_${selectedOffer?.offer_id}`}
                className={classNames(styles.card, {
                    [styles.disabled]: !offersInRange()
                })}
            >
                <h1 className={styles.title}>
                    <FormattedMessage
                        {...messages.title}
                        values={{
                            noRepayments:
                                schedule.data.summary
                                    .total_number_of_repayments,
                            button: (chunks) => (
                                <button
                                    className={styles.titleButton}
                                    onClick={() => setOpenSchedule(true)}
                                >
                                    {chunks}
                                </button>
                            )
                        }}
                    />
                </h1>

                <AlertBox variant="success" className={styles.alert}>
                    <Icon icon="starFilled" className={styles.alertIcon} />
                    <span>
                        <FormattedMessage
                            {...benefitMessage()}
                            values={{
                                bold: (chunks) => <strong>{chunks}</strong>
                            }}
                        />
                    </span>
                </AlertBox>

                <div className={styles.info}>
                    <div className={styles.infoRow}>
                        <div>
                            <FormattedMessage {...pricingMessage()} />
                        </div>
                        <div>
                            <FormattedMessage
                                {...interestMessage()}
                                values={{
                                    interest: intl.formatNumber(
                                        Math.round(
                                            (selectedOffer?.interest_rates?.[0]
                                                .rate || 0) * 10000
                                        ) / 100
                                    ),
                                    fetching: (chunks) => (
                                        <Fetching isFetching={isFetching}>
                                            {chunks}
                                        </Fetching>
                                    )
                                }}
                            />
                        </div>
                    </div>
                    <div className={styles.infoRow}>
                        <div>
                            <FormattedMessage {...messages.repayments} />
                        </div>
                        <div>
                            <FormattedMessage
                                {...messages.repaymentAmount}
                                values={{
                                    amount: intlFormatCurrency({
                                        intl,
                                        value:
                                            schedule?.data.summary
                                                .equal_repayment_amount || 0
                                    }),
                                    fetching: (chunks) => (
                                        <Fetching isFetching={isFetching}>
                                            {chunks}
                                        </Fetching>
                                    )
                                }}
                            />
                            <Button
                                variant="tertiary"
                                size="small"
                                className={styles.viewSchedule}
                                onClick={() => setOpenSchedule(true)}
                            >
                                <FormattedMessage {...messages.viewSchedule} />
                            </Button>
                        </div>
                    </div>
                    <div className={styles.infoRow}>
                        <div>
                            <FormattedMessage {...messages.total} />
                        </div>
                        <div>
                            <Fetching isFetching={isFetching}>
                                {intlFormatCurrency({
                                    intl,
                                    value:
                                        schedule?.data.summary.total_payable ||
                                        0
                                })}
                            </Fetching>
                        </div>
                    </div>
                </div>

                <div>
                    {isMultiOffer && offers.length > 1 && (
                        <div className={styles.collapsable}>
                            <div className={styles.durationLabel}>
                                <FormattedMessage
                                    {...messages.selectDuration}
                                />
                                <div className={styles.radioDurations}>
                                    {offers
                                        ?.sort(
                                            (a, b) =>
                                                a.duration!.amount -
                                                b.duration!.amount
                                        )
                                        ?.map((offer) => {
                                            const disbaled =
                                                drawdownAmount >
                                                    offer.max_amount! ||
                                                drawdownAmount < 150;
                                            const term =
                                                getLoanDurationInMonths(
                                                    offer.duration!
                                                );
                                            return (
                                                <Tooltip
                                                    key={`radio_${offer.offer_id}`}
                                                    open={
                                                        disbaled
                                                            ? undefined
                                                            : false
                                                    }
                                                    placement="top"
                                                    content={intl.formatMessage(
                                                        messages.durationNotAvailable,
                                                        {
                                                            term: term,
                                                            min: intlFormatCurrency(
                                                                {
                                                                    intl,
                                                                    value: 150
                                                                }
                                                            ),
                                                            max: intlFormatCurrency(
                                                                {
                                                                    intl,
                                                                    value: offer.max_amount!
                                                                }
                                                            )
                                                        }
                                                    )}
                                                >
                                                    <Radio
                                                        name={intl.formatMessage(
                                                            messages.duration,
                                                            {
                                                                amount: term
                                                            }
                                                        )}
                                                        label={intl.formatMessage(
                                                            messages.duration,
                                                            {
                                                                amount: term
                                                            }
                                                        )}
                                                        checked={
                                                            offer.offer_id ===
                                                            selectedOffer?.offer_id
                                                        }
                                                        onClick={() =>
                                                            setSelectedOffer(
                                                                offer
                                                            )
                                                        }
                                                        disabled={disbaled}
                                                        frame
                                                    />
                                                </Tooltip>
                                            );
                                        })}
                                </div>
                            </div>
                        </div>
                    )}
                    <div className={styles.collapseControll}>
                        <Button
                            onClick={() => {
                                tracking.ampli.drawdownCtaClicked();
                                selectedOffer?.offer_id &&
                                    startDrawdown(
                                        selectedOffer.offer_id,
                                        drawdownAmount
                                    );
                            }}
                            disabled={!offersInRange()}
                        >
                            <FormattedMessage {...messages.continue} />
                        </Button>
                    </div>
                </div>
            </Card>
            <FlexiScheduleModal
                schedule={schedule.data.schedule}
                isOpen={openSchedule}
                onClose={() => setOpenSchedule(false)}
            />
        </>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(FlexiCard);
